import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { db } from "../firebase/firebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { UserContext } from "../context/UserContext";
import { toast } from "react-toastify";

export default function AddSkills() {
  const { user, setUser } = useContext(UserContext);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const allSkills = [
    "JavaScript",
    "TypeScript",
    "Python",
    "Java",
    "C++",
    "React",
    "Node.js",
    "Express.js",
    "MongoDB",
    "Firebase",
    "Next.js",
    "Kotlin",
    "Swift",
    "Flutter",
    "TensorFlow",
    "AWS",
    "Docker",
    "GitHub Actions",
    "PostgreSQL",
    "MySQL",
    "TailwindCSS",
    "Redux",
    "GraphQL",
    "Django",
    "Laravel",
    "Solidity",
    "Web3.js",
    "Figma",
    "VS Code",
  ];

  useEffect(() => {
    getUserSkills();
  }, []);

  const getUserSkills = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const userDocRef = doc(db, "users", user.id);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSelectedSkills(data.skills || []);
        setUser(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSkill = (skill) => {
    if (!selectedSkills.includes(skill)) {
      setSelectedSkills([...selectedSkills, skill]);
    }
    setSearch("");
  };

  const handleRemoveSkill = (skill) => {
    setSelectedSkills(selectedSkills.filter((s) => s !== skill));
  };

  const handleSubmit = async () => {
    if (!user) return;
    const userRef = doc(db, "users", user.id);
    await setDoc(userRef, { skills: selectedSkills }, { merge: true });
    toast.success("✅ Skills updated successfully!");
    getUserSkills();
  };

  const filteredSkills = allSkills.filter(
    (s) =>
      s.toLowerCase().includes(search.toLowerCase()) &&
      !selectedSkills.includes(s)
  );

  return (
    <Container>
      {loading && (
        <LoadingOverlay>
          <Loader />
        </LoadingOverlay>
      )}

      <Card>
        <Title>💡 Add Your Skills</Title>

        <SearchWrapper>
          <SearchInput
            placeholder="Search your skills..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <DropdownList>
              {filteredSkills.length > 0 ? (
                filteredSkills.map((skill, i) => (
                  <Option key={i} onClick={() => handleSelectSkill(skill)}>
                    {skill}
                  </Option>
                ))
              ) : (
                <Option>No matching skills found</Option>
              )}
            </DropdownList>
          )}
        </SearchWrapper>

        <ChipsWrapper>
          {selectedSkills.length === 0 && (
            <PlaceholderText>No skills added yet</PlaceholderText>
          )}
          {selectedSkills.map((skill, i) => (
            <Chip key={i}>
              {skill}
              <X
                size={14}
                onClick={() => handleRemoveSkill(skill)}
                style={{ cursor: "pointer" }}
              />
            </Chip>
          ))}
        </ChipsWrapper>

        <Button onClick={handleSubmit}>Save Skills</Button>
      </Card>
    </Container>
  );
}

/* ----------------- Styled Components ----------------- */

const Container = styled.div`
  background: radial-gradient(circle at top left, #0b0617, #000);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 20px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(18px);
  border-radius: 20px;
  padding: 30px;
  width: 100%;
  max-width: 600px;
  color: #fff;
  box-shadow: 0 0 30px rgba(155, 92, 255, 0.15);
`;

const Title = styled.h2`
  text-align: center;
  font-size: 24px;
  margin-bottom: 20px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 15px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 15px;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: rgba(155, 92, 255, 0.6);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.3);
  }
`;

const DropdownList = styled.div`
  position: absolute;
  top: 110%;
  width: 100%;
  max-height: 180px;
  overflow-y: auto;
  background: rgba(20, 20, 20, 0.95);
  border: 1px solid rgba(155, 92, 255, 0.2);
  border-radius: 10px;
  z-index: 10;
  backdrop-filter: blur(8px);

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(155, 92, 255, 0.2);
    border-radius: 10px;
  }
`;

const Option = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s;
  color: #fff;

  &:hover {
    background: rgba(155, 92, 255, 0.25);
  }
`;

const ChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  min-height: 40px;
  padding: 10px 0;
  max-height: 200px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(155, 92, 255, 0.3);
    border-radius: 10px;
  }
`;

const Chip = styled.div`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 0 10px rgba(155, 92, 255, 0.2);
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const PlaceholderText = styled.div`
  color: #777;
  font-size: 14px;
`;

const Button = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 12px;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s ease;
  margin-top: 20px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.4);
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Loader = styled.div`
  border: 6px solid rgba(255, 255, 255, 0.2);
  border-top: 6px solid #a855f7;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    100% {
      transform: rotate(360deg);
    }
  }
`;
